/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import $ from '@/jquery'
import _ from '@/underscore'
import Backbone from '@/backbone'
import ox from '@/ox'
import http from '@/io.ox/core/http'
import upsell from '@/io.ox/core/upsell'
import capabilities from '@/io.ox/core/capabilities'
import ext from '@/io.ox/core/extensions'
import icons from '@/io.ox/core/main/icons'
import Dropdown from '@/io.ox/backbone/mini-views/dropdown'
import '@/io.ox/core/main/autologout'
import apps from '@/io.ox/core/api/apps'
import theming from '@/io.ox/core/theming/main'
import logout from '@/io.ox/core/main/logout'
import * as helpUtil from '@/io.ox/help/util'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { createIcon, createIndicator } from '@/io.ox/core/components'
import { controller as notificationAreaController } from '@/io.ox/core/notifications/main'
import tabApi from '@/io.ox/core/api/tab'
import gt from 'gettext'
import openSettings from '@/io.ox/settings/util'
import { UserView } from '@/io.ox/core/main/userView'
import { onLongPress } from '@vueuse/core'

const isStandaloneBrowserTab = ox.openedInBrowserTab || (tabApi.openInTabEnabled() && _.url.hash('app') === 'io.ox/files/detail')

function toggleOverlay (force) {
  $('#io-ox-appcontrol').toggleClass('open', force)
  $('#io-ox-launchgrid-overlay, #io-ox-launchgrid-overlay-inner').toggle(force)
}

const LauncherView = Backbone.View.extend({
  tagName: 'button',
  className: 'btn btn-toolbar btn-topbar',
  attributes: {
    role: 'menuitem',
    tabindex: -1
  },
  events: {
    click: 'onClick',
    'click .closer': 'quitApp'
  },
  initialize (options) {
    this.pos = options.pos
    this.quicklaunch = options.quicklaunch
    this.hasLabel = options.hasLabel
    this.listenTo(this.model, 'change:hasBadge', this.toggleBadge)
    this.listenTo(this.model, 'change:tooltip', this.updateTooltip)
    this.listenTo(this.model, 'change:title', this.updateTitle)
    this.listenTo(coreSettings, 'change:coloredIcons', this.render)
    this.$cell = $()
  },
  addAccessKey () {
    if (this.pos <= 9 && this.pos !== 0) this.$el.attr('accesskey', this.pos)
  },
  checkUpsell () {
    const requires = this.model.get('requires')
    return !upsell.has(requires)
  },
  drawUpsellIcon (elem) {
    if (!this.checkUpsell()) return
    elem.addClass('upsell').append(createIcon(upsell.getDefaultIcon()).addClass('upsell-icon'))
  },
  async onClick () {
    if (!this.checkUpsell()) {
      // close notification area when an app is launched. This is especially important on mobile, otherwise users will have apps opened in the background without noticing them. See OXUIB-1880
      notificationAreaController.trigger('hide')

      if (this.model.options.mobilelazyload) {
        // WORKAROUND: OXUIB-932 - lazyload current edited drafts on mobile
        return this.model.trigger('mobilelazyload')
      }
      // used on mobile
      if (this.model.get('state') === 'running' && this.model.get('closable')) {
        this.model.launch()
        return
      }
      if (this.model.get('staticUrl')) {
        window.open(this.model.get('staticUrl'), '_blank')
        return
      }
      if (tabApi.openInTabEnabled() && this.model.get('openInTab')) {
        // we must stay synchronous to prevent popup-blocker
        tabApi.openChildTab(this.model.get('tabUrl'))
        return
      }

      // if there is a custom quicklaunch function for this app, use it. Else just use the default launch function
      if (this.quicklaunch && this.model.quickLaunch) this.model.quickLaunch()
      else ox.launch(this.model.load)
    } else {
      const requires = this.model.get('requires')
      upsell.trigger({ type: 'app', id: this.model.get('id'), missing: upsell.missing(requires) })
    }
  },
  quitApp (e) {
    // used on mobile
    e.preventDefault()
    e.stopPropagation()
    this.model.quit()
  },
  render () {
    this.$el.empty().attr({
      'data-id': this.model.get('id'),
      'data-app-name': this.model.get('name')
    }).toggleClass('active', ox.ui.App.isCurrent(this))
    this.getIcon()
    this.badge = createIndicator()
      .toggleClass('hidden', !this.model.get('hasBadge'))
    if (this.quicklaunch) this.renderQuicklaunchIcon()
    else this.renderDropdownIcon()
    this.updateTooltip()
    this.addAccessKey()
    return this
  },
  renderQuicklaunchIcon () {
    this.$cell = $('<div class="lcell" aria-hidden="true">').append(this.badge, this.$icon)
    this.$el.append(this.$cell)
    this.drawUpsellIcon(this.$el)
  },
  renderDropdownIcon () {
    const appColor = this.model.get('userContentIconColor') || theming.getAppColor(this.model.get('name'))
    this.$cell = $('<div class="lcell">').append(
      this.badge,
      $('<div class="icon-wrap flex-center" aria-hidden="true">').append(
        $('<div class="icon-background">').css('backgroundColor', appColor),
        this.$icon.css('color', appColor)
      ),
      $('<div class="title">').text(this.model.get('title'))
    )
    // checks for upsell and append an icon if needed
    this.drawUpsellIcon(this.$cell.find('.title'))
    this.$el.append(this.$cell)
  },
  getIcon () {
    let icon = this.model.get('icon')
    // check for compose apps
    if (this.model.get('closable') && _.device('smartphone')) {
      icon = ox.ui.appIcons[this.model.options.name]
      // some apps have icons for the taskbar, better than using the fallback
      const userIcon = this.model.options.userContentIcon
      if (!icon && userIcon) {
        // support for SVG markup string, or DOM elements, or JQuery objects
        icon = _.isString(userIcon) ? createIcon(userIcon) : $(userIcon)
      }
    }
    this.$icon = icon ? icon.clone() : icons.fallback.clone()
  },
  toggleBadge () {
    this.badge.toggleClass('hidden', !this.model.get('hasBadge'))
  },
  updateTooltip () {
    if (!this.quicklaunch || this.hasLabel) return
    let tooltip = this.model.get('tooltip')
    const title = this.model.get('title')
    tooltip = title + (tooltip ? ' (' + tooltip + ')' : '')
    this.$el.attr('aria-label', tooltip)
    this.$cell.attr({ 'aria-hidden': true, title: tooltip })
  },
  updateTitle (model, newTitle) {
    const $title = this.$el.find('.title')
    $title.text(newTitle)
    this.drawUpsellIcon($title)
  }
})

function getQuickLaunchSettingsKey () {
  return `apps/quickLaunch${_.device('smartphone') ? 'Mobile' : ''}`
}

function getQuickLauncherItems () {
  const isMobile = _.device('smartphone')
  const count = getQuickLauncherCount()
  const defaults = isMobile
    ? ['io.ox/mail', 'io.ox/calendar', 'io.ox/contacts']
    : ['io.ox/mail', 'io.ox/calendar', 'io.ox/files']
  const exists = app => app.includes('customQuickLaunchers') || apps.get(app)

  const rawList = String(coreSettings.get(getQuickLaunchSettingsKey(), defaults))
    .trim()
    .split(/,\s*/)
    .map(app => app.replace(/\/main$/, ''))

  if (isMobile) {
    // mobile: merge user preference with defaults, no padding with 'none'
    const userQuickLaunchers = rawList.filter(s => s && s !== 'none')
    return Array.from(new Set([...userQuickLaunchers, ...defaults]))
      .slice(0, count)
      .filter(exists)
  }

  // desktop: honor user list (or defaults) and pad with 'none'
  const filtered = rawList.filter(exists).join(',')
  return ((filtered || 'none') + new Array(count).join(',none'))
    .split(',')
    .slice(0, count)
}

function getQuickLauncherCount () {
  const n = _.device('smartphone')
    ? Math.min(coreSettings.get('apps/quickLaunchCountMobile', 3), 3)
    : coreSettings.get('apps/quickLaunchCount', 3)
  if (!_.isNumber(n)) return 0
  return Math.min(api.quickLaunchLimit, apps.forLauncher().length, n)
}

function isQuickLaunchConfigurable () {
  return coreSettings.isConfigurable(getQuickLaunchSettingsKey()) && getQuickLauncherCount() !== 0
}

const api = {
  quickLaunchLimit: 5,
  isQuickLaunchConfigurable,
  getQuickLaunchSettingsKey,
  getQuickLauncherItems,
  getQuickLauncherCount
}

// reverted for 7.10
const QuickLaunchersCollection = Backbone.Collection.extend({
  initialize () {
    this.reload()
    coreSettings.on('change:apps/quickLaunch change:apps/quickLaunchCount change:apps/quickLaunchMobile change:apps/quickLaunchCountMobile', this.reload.bind(this))
    this.listenTo(apps, 'add reset', this.reload)
  },
  reload () {
    this.reset(this.fetchLaunchers())
  },
  fetchLaunchers () {
    return api.getQuickLauncherItems()
      .map(o => {
        if (o.indexOf('customQuickLaunchers') !== -1) {
          return {
            isCustomLauncher: true,
            extensionId: o.replace('io.ox/core/appcontrol/customQuickLaunchers/', '')
          }
        }
        return apps.get(o.replace(/\/main$/, ''))
      })
      .filter(Boolean)
  }
})

// reverted for 7.10
const QuickLaunchersView = Backbone.View.extend({
  attributes: {
    id: 'io-ox-quicklaunch',
    role: 'toolbar'
  },
  events: {
    'click button': 'onClick',
    contextmenu: 'onContextmenu'
  },
  initialize () {
    this.collection = new QuickLaunchersCollection()
    this.listenTo(this.collection, { reset: this.render })
  },
  onClick () {
    toggleOverlay(false)
  },
  onContextmenu (e) {
    e.preventDefault()
    if (coreSettings.isConfigurable('apps/quickLaunch') && api.getQuickLauncherCount() !== 0 && !_.device('smartphone')) {
      import('@/io.ox/core/settings/dialogs/quickLauncherDialog').then(function ({ default: quickLauncherDialog }) {
        quickLauncherDialog.openDialog()
      })
    }
  },
  render () {
    this.$el.empty().append(
      this.collection.map(function (model) {
        if (model.get('isCustomLauncher')) {
          // something broken? just leave this one out
          if (!ext.point('io.ox/core/appcontrol/customQuickLaunchers').get(model.get('extensionId'))) return ''
          return ext.point('io.ox/core/appcontrol/customQuickLaunchers').get(model.get('extensionId')).draw()
        }

        // app has requirements that are not met and are not available for upsell
        const requires = model.get('requires')
        if (requires && !upsell.available(requires)) return ''

        return new LauncherView({
          tagName: 'button',
          attributes: { tabindex: -1, type: 'button' },
          model,
          quicklaunch: true
        }).render().$el.attr('tabindex', -1)
      })
    )
    this.$el.find('button').first().removeAttr('tabindex')
    return this
  }
})

ext.point('io.ox/core/appcontrol/sideview').extend({
  id: 'edit-personal-data-mobile',
  index: 20,
  draw () {
    if (coreSettings.get('user/internalUserEdit', true) === false) return

    this.$el.append(this.createMenuButton({
      device: 'smartphone',
      name: 'my-contact-data',
      text: gt('Edit personal data'),
      icon: 'person',
      async action () {
        const userSettings = (await import('@/io.ox/core/settings/user')).default
        userSettings.openModalDialog()
      }
    })
    )
  }
}, {
  id: 'settings-mobile',
  index: 40,
  draw () {
    this.$el.append(this.createMenuButton({
      device: 'smartphone',
      name: 'settings',
      text: gt('Settings'),
      icon: 'gear',
      action: () => openSettings()
    }))
  }
}, {
  id: 'onboarding-mobile',
  index: 60,
  enabled: capabilities.has('client-onboarding'),
  draw () {
    if (!coreSettings.get('onboardingWizard')) return
    this.$el.append(this.createMenuButton({
      device: 'smartphone',
      name: 'onboarding',
      icon: 'phone',
      text: gt('Connect this device'),
      async action () {
        const onboardingWizard = (await import('@/io.ox/onboarding/main')).default
        onboardingWizard.load()
      }
    }))
  }
}, {
  id: 'help',
  index: 100,
  draw () {
    this.$el.append(this.createMenuButton({
      name: 'help',
      icon: _.device('smartphone') ? 'question-circle' : undefined,
      text: gt('Help'),
      async action () {
        const HelpApp = (await import('@/io.ox/help/main')).default
        const opt = { href: helpUtil.getHelp() }

        if (HelpApp.reuse(opt)) return
        HelpApp.getApp(opt).launch()
      }
    }))
  }
}, {
  id: 'settings',
  index: 300,
  draw () {
    this.$el.append(this.createMenuButton({
      device: '!smartphone',
      name: 'settings',
      text: gt('Settings'),
      action: () => openSettings()
    })
    )
  }
}, {
  id: 'about-mobile',
  index: 500,
  draw () {
    if (!_.device('smartphone')) return
    const menuItem = this.createMenuButton({
      device: 'smartphone',
      name: 'about',
      text: gt('About'),
      icon: 'info-circle',
      action (event) { helpUtil.getAbout(event) }
    })
    this.$el.append(menuItem)
    onLongPress(menuItem.get(0), event => {
      event.longPress = true
      helpUtil.getAbout(event)
      // close launcher:
      this.$el.closest('.launcher:has(> .launcher-dropdown)').trigger('click')
    }, { delay: 1000 })
  }
}, {
  id: 'logout',
  index: 'last',
  draw () {
    this.$el.append(
      // using a different name not to confuse e2e tests
      this.createMenuButton({
        name: 'launcher-logout',
        text: gt('Sign out'),
        icon: _.device('smartphone') ? 'box-arrow-right' : undefined,
        action (event) {
          event.preventDefault()
          logout()
        }
      })
    )
  }
})

const LauncherActionView = Backbone.View.extend({
  tagName: 'li',
  className: 'menu-actions',
  events: {
    'click button.launcher-action': 'onClickAction'
  },
  createMenuButton ({ device, icon, text, action, name }) {
    if (device && !_.device(device)) return

    const $button = $('<button class="launcher-action btn btn-toolbar ellipsis" draggable="false" tabindex="-1" role="menuitem">')
      .attr({ 'data-name': name || '' })

    if (icon) $button.append(createIcon(`bi/${icon}.svg`).addClass('shrink-0 bi-24 mr-16 align-middle'))
    if (text) $button.append($('<span>').text(text))
    if (typeof action === 'function') $button.on('click', action)
    // On smartphones, stop propagation so Dropdown.onClick on $ul doesn't call close().
    // This keeps the launcher dropdown open behind full-screen views (e.g. settings)
    // and avoids a spurious toggleDropdown history entry that breaks back/forward navigation.
    if (_.device('smartphone')) $button.on('click', (e) => e.stopPropagation())

    return $('<li role="presentation">').append($button)
  },
  render () {
    this.$el.empty()
    ext.point('io.ox/core/appcontrol/sideview').invoke('draw', this, ext.Baton({}))
    this.$el.attr('role', 'none')
    return this
  }
})

// hint: shared node references so multiple view instances won't work
const LaunchersView = Dropdown.extend({
  attributes: { role: 'presentation' },
  tagName: 'li',
  className: 'launcher dropdown',
  id: 'io-ox-launcher',
  $ul: $('<ul class="launcher-dropdown dropdown-menu">'),
  $menu: $('<div class="menu" role="group">'),
  // this should be a link. Otherwise, this can cause strange focus issues on iOS when having the cursor inside an iframe before clicking this (see Bug 63441)
  $toggle: $('<button type="button" class="btn btn-toolbar btn-topbar dropdown-toggle launcher-btn" tabindex="0">')
    .attr('aria-label', gt('Navigate to:'))
    .append($('<div aria-hidden="true">').attr('title', gt('All Applications')).append($(icons.launcher).addClass('larger'))),
  initialize () {
    Dropdown.prototype.initialize.apply(this, arguments)
    // Overwriting keydown event from @open-xchange/bootstrap/src/dropdown as it only works for <a>
    this.$ul.on('keydown', 'button', this.onKeyDown)

    const appsView = new AppsView(this.collection)
    this.$ul.append(appsView.render().$el)
    this.$apps = appsView.$apps

    if (_.device('smartphone')) {
      this.$ul.prepend(
        new UserView({
          tagName: 'div',
          editable: coreSettings.get('user/internalUserEdit', true),
          ellipsis: false
        }).render().$el)
      this.$ul.append(new DraftsView(this.collection).render().$el)
    }
    this.$ul.append(this.$menu)
    this.$menu.empty().append(new LauncherActionView().render().$el)

    // disable closing by clicking the menus void
    this.$ul.on('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
    })

    this.$ul.one('transitionend', () => {
      this.$ul.addClass('complete')
    })
  },

  onKeyDown (e) {
    if (e.which === 38 || e.which === 40) {
      e.preventDefault()
      e.stopPropagation()
      const $target = $(e.target)
      const $list = $target.closest('ul').find('button:visible[role^="menuitem"]')
      let index = $list.index($target)

      if (e.which === 38) index--
      if (e.which === 40) index++

      if (index < 0) index += $list.length
      if (index >= $list.length) index -= $list.length

      _.defer(function () {
        $list.eq(index).get(0).focus()
      })
    }
  },
  dispose () {
    this.$ul.off('keydown', 'button', this.onKeyDown)
    Dropdown.prototype.dispose.apply(this, arguments)
  }
})

const AppsView = Backbone.View.extend({
  $apps: $('<div class="apps" role="group">'),
  initialize (collection) {
    this.collection = collection
  },
  render () {
    this.$el = $('<div>)')
      .toggleClass('mb-16', _.device('smartphone'))
      .append(
        $('<h3 class="mb-16 mt-0">').text(gt('Apps')),
        this.$apps
      )

    const update = () => {
      this.$apps.empty().append(
        this.collection.forLauncher().map((model, i) => {
          return $('<li role="presentation">').append(new LauncherView({ model, pos: i + 1 }).render().$el)
        })
      )

      // draw custom launchers. Some items that appear in the launcher are not full apps, like the enterprise picker dialog
      ext.point('io.ox/core/appcontrol/customLaunchers').invoke('draw', this.$apps)
    }

    this.listenTo(this.collection, 'add remove launcher:update launcher:sort', update)
    update()

    return this
  }
})

const DraftsView = Backbone.View.extend({
  $drafts: $('<div class="drafts" role="group">'),
  initialize (collection) {
    this.collection = collection
  },
  render () {
    this.$el = $('<div class="mb-16">').append(
      $('<h3 class="mb-16 mt-0">').text(gt('Drafts')),
      this.$el.append(this.$drafts)
    )

    const update = () => {
      this.$el.addClass('hidden').attr('aria-hidden', 'true')
      this.$drafts.empty().append(
        this.collection.where({ closable: true }).map(model => {
          this.$el.removeClass('hidden').removeAttr('aria-hidden')
          return $('<li role="presentation">').append(new LauncherView({ model }).render().$el)
        })
      )
    }

    this.listenTo(this.collection, 'add remove launcher:update launcher:sort', update)
    update()

    return this
  }
})

ext.point('io.ox/core/appcontrol').extend({
  id: 'init',
  index: 100,
  draw () {
    let overlay
    $('#io-ox-core').append(
      overlay = $('<div id="io-ox-launchgrid-overlay">').on('click', toggleOverlay)
    )
    if (_.device('smartphone')) {
      overlay.on('touchstart', function (e) {
        e.preventDefault()
        toggleOverlay()
      })

      apps.on('show:appcontrol', _.debounce(function (model) {
        if (!model.pages) return $('#io-ox-appcontrol').hide()
        const currentPage = model.pages.getCurrentPage()
        const secondary = model.props.get('checkboxes')
        model.pages.showAppcontrol(currentPage.name, secondary)
      }))
    }
    initRefreshAnimation()

    apps.on('launch resume', function (model) {
      if (model.get('floating')) return

      $('.launcher-dropdown').find('.lcell[data-app-name]')
        .removeClass('active').end()
        .find(`.lcell[data-app-name="${CSS.escape(model.get('name'))}"]`).addClass('active')

      $('#io-ox-quicklaunch').find('button[data-id]')
        .removeClass('active').end()
        .find(`button[data-id="${CSS.escape(model.get('name'))}"]`).addClass('active')

      $('#app-navigation').find('button[data-id]')
        .removeClass('active').end()
        .find(`button[data-id="${CSS.escape(model.get('name'))}"]`).addClass('active')

      _.defer(function () {
        $(document).trigger('resize')
      })
    })
  }
})

ext.point('io.ox/core/appcontrol/left').extend({
  id: 'skiplinks',
  index: 100,
  draw () {
    this.append(
      $('<a class="skip-links sr-only sr-only-focusable" href="#">').append(
        $('<span>').text(gt('Skip to main content'))
      )
    )
  }
})

ext.point('io.ox/core/appcontrol/left').extend({
  id: 'logo',
  index: 200,
  draw () {
    const logoTopbarElement = $('<div id="io-ox-top-logo" class="me-8 ms-10">').on('click', 'button[data-action]', clickButton)
    const logoContainer = $('<div class="logo-container p-8" aria-hidden="true">')
    const logoButton = $('<button type="button" class="logo-btn btn btn-link" data-action="">')
    const logoLink = $('<a class="btn btn-link logo-btn" target="_blank">')

    function clickButton (e) {
      const action = $(e.currentTarget).attr('data-action')
      const app = apps.get(action.replace(/\/main$/, ''))
      if (!app) return
      notificationAreaController.trigger('hide')
      ox.launch(app.load)
    }

    function getAction () {
      const autoStart = coreSettings.get('autoStart', 'io.ox/mail')
      const actionSetting = coreSettings.get('logoAction', 'autoStart')
      const action = actionSetting === 'autoStart' ? autoStart : actionSetting

      if (!action || action === 'none' || (action === 'autoStart' && autoStart === 'none')) return { el: 'none' }
      if (isStandaloneBrowserTab) return { type: 'none' }
      if ((/^https?:/).test(action)) return { type: 'link', action }
      if (tabApi.openInTabEnabled() && (/^io\.ox\/office\/portal/).test(action)) {
        const url = tabApi.createUrl({ app: action }, { exclude: 'folder', suffix: `office?app=${action.substring(0, 20)}` })
        return { type: 'link', action: url }
      }
      if (action) return { type: 'button', action }
    }

    function renderLogo () {
      const { type, action } = getAction()
      const title = getTitle(type)
      logoContainer.attr('title', title)
      logoTopbarElement.empty()
      switch (type) {
        case 'button':
          logoButton.attr({ 'aria-label': title, 'data-action': action })
          return logoTopbarElement.append(logoButton.append(logoContainer))
        case 'link':
          logoLink.attr({ href: action, 'aria-label': title })
          return logoTopbarElement.append(logoLink.append(logoContainer))
        default:
          return logoTopbarElement.append(logoContainer)
      }
    }

    function getTitle (type = getAction().type) {
      switch (type) {
        case 'button': return coreSettings.get('logoTitle', gt('Open default app'))
        case 'link': return coreSettings.get('logoTitle', ox.serverConfig.productName)
        default: return null
      }
    }

    this.append(renderLogo())
    theming.renderLogo(logoContainer)

    if (isStandaloneBrowserTab) return

    coreSettings.on('change:logoAction change:autoStart', () => {
      $('#io-ox-top-logo').replaceWith(renderLogo())
    })
  }
})
ext.point('io.ox/core/appcontrol').extend({
  id: 'apptitle',
  index: 300,
  draw (baton) {
    const $header = $('<h1 class="sr-only">')
    ox.on('app:start app:resume', e => $header.text(e.options.title))
    this.append($header)
  }
})

ext.point('io.ox/core/appcontrol').extend({
  id: 'left',
  index: 350,
  draw () {
    if (_.device('smartphone')) return
    const $el = $('<div id="io-ox-topleftbar" class="justify-start flex-grow me-auto">')
    this.append($el)
    ext.point('io.ox/core/appcontrol/left').invoke('draw', $el)
  }
})

// reverted for 7.10
ext.point('io.ox/core/appcontrol/left').extend({
  id: 'quicklauncher',
  index: 400,
  draw () {
    if (_.device('smartphone')) return
    if (isStandaloneBrowserTab) return
    const quicklaunchers = window.quicklaunchers = new QuickLaunchersView()
    this.append(quicklaunchers.render().$el)
  }
})

if (!_.device('smartphone') && coreSettings.get('features/enterprisePicker/enabled', false)) {
  const openPicker = _.debounce(async function () {
    const popup = (await import('@/io.ox/contacts/enterprisepicker/dialog')).default
    popup.open($.noop, { selection: { behavior: 'none' } })
  }, 300, true)

  ext.point('io.ox/core/appcontrol/right').extend({
    id: 'enterprisePicker',
    index: 130,
    draw () {
      if (!coreSettings.get('features/enterprisePicker/showTopRightLauncher', false)) return ''

      const node = $('<li role="presentation" class="launcher">').append(
        $('<button id="io-ox-enterprise-picker-icon" class="launcher-btn btn btn-toolbar btn-topbar" tabindex="-1">').attr('aria-label', gt('Address directory')).append(createIcon('bi/journal-text.svg'))
          .on('click', openPicker)
      )
      this.append(node)
    }
  })

  ext.point('io.ox/core/appcontrol/customLaunchers').extend({
    id: 'enterprisePicker',
    index: 100,
    draw () {
      if (!coreSettings.get('features/enterprisePicker/showLauncher', true)) return ''

      this.append(
        $('<li role="presentation">').append(
          $('<button tabindex="-1" role="menuitem" class="btn btn-toolbar btn-topbar">').append(
            $('<div class="lcell">').append(
              // just use same color as contacts
              $('<div class="icon-wrap flex-center" aria-hidden="true">').append(
                $('<div class="icon-background">').css('backgroundColor', theming.getAppColor('io.ox/contacts')),
                createIcon('bi/journal-text.svg').css('color', theming.getAppColor('io.ox/contacts'))
              ),
              $('<div class="title">').text(gt('Address directory'))
            )
          )
            .on('click', openPicker)
            .on('keydown', e => e.key === 'Enter' && openPicker(e))
        )
      )
    }
  })

  ext.point('io.ox/core/appcontrol/customQuickLaunchers').extend({
    id: 'enterprisePicker',
    label: gt('Address directory'),
    index: 100,
    draw () {
      if (!coreSettings.get('features/enterprisePicker/showLauncher', true)) return ''

      return $('<button tabindex="-1" type="button" class="btn btn-topbar btn-toolbar" role="menuitem" tabindex="-1">').attr('aria-label', gt('Address directory')).append(
        $('<div aria-hidden="true">').attr('title', gt('Address directory')).append(createIcon('bi/journal-text.svg'))
      ).on('click', openPicker)
    }
  })
}

// deactivated since 7.10.0
ext.point('io.ox/core/appcontrol').extend({
  id: 'search',
  index: 500,
  draw () {
    if (_.device('smartphone')) return
    const search = $('<div id="io-ox-topsearch" class="flex-center">')
    this.append(search)
  }
})

ext.point('io.ox/core/appcontrol').extend({
  id: 'right',
  index: 600,
  draw () {
    if (_.device('smartphone')) return
    const taskbar = $('<ul class="taskbar list-unstyled" role="toolbar">').toggleClass('me-16', !_.device('smartphone'))
    this.append($('<div id="io-ox-toprightbar" class="justify-end flex-grow ms-auto">').append(taskbar))
    ext.point('io.ox/core/appcontrol/right').invoke('draw', taskbar)
  }
})

ext.point('io.ox/core/appcontrol').extend({
  id: 'show',
  index: 10000,
  draw () {
    this.attr('role', 'banner').show()
  }
})

ext.point('io.ox/core/appcontrol/left').extend({
  id: 'launcher',
  index: 300,
  draw () {
    if (_.device('smartphone')) return
    const taskbar = $('<ul class="taskbar list-unstyled m-0" role="toolbar">')
    const appLauncher = window.launchers = new LaunchersView({ collection: apps })
    taskbar.append(appLauncher.render().$el)
    this.append(taskbar)
  }
})

const AppNavigationView = Backbone.View.extend({
  tagName: 'ul',
  className: 'flex items-center justify-around list-unstyled p-4',
  attributes: {
    role: 'toolbar',
    id: 'app-navigation'
  },
  initialize (collection) {
    this.collection = new QuickLaunchersCollection()
    this.listenTo(this.collection, { reset: this.refreshApps })
  },
  renderAppLaunchers () {
    const tabs = this.collection.map(model => {
      if (model.get('isCustomLauncher')) {
        // something broken? just leave this one out
        if (!ext.point('io.ox/core/appcontrol/customQuickLaunchers').get(model.get('extensionId'))) return ''
        return ext.point('io.ox/core/appcontrol/customQuickLaunchers').get(model.get('extensionId')).draw()
      }

      // app has requirements that are not met and are not available for upsell
      const requires = model.get('requires')
      if (requires && !upsell.available(requires)) return ''

      const launcherView = new LauncherView({
        tagName: 'button',
        attributes: { tabindex: -1, type: 'button' },
        className: 'btn btn-toolbar flex-col',
        model,
        quicklaunch: true,
        hasLabel: true
      })

      const tab = $('<li role="presentation">').append(
        launcherView.render().$el.append(
          $('<span class="icon-label pt-6">').text(model.get('title'))
        )
      )
      return tab
    })

    return tabs
  },
  refreshApps () {
    const $appLaunchers = this.renderAppLaunchers()

    this.$el.find('> li:has(> [data-app-name])').remove()
    this.$el.prepend($appLaunchers)
  },
  render () {
    const appLauncher = window.launchers = new LaunchersView({ collection: apps })

    appLauncher.$el.on('shown.bs.dropdown', () => {
      const height = appLauncher.$ul.height() + 32
      const minTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top'), 10) || 0
      const newTop = height > innerHeight ? minTop : innerHeight - height
      appLauncher.$ul.css({ top: '100%', display: '' })
      appLauncher.$ul.stop(true).animate({ top: newTop + 'px' }, {
        duration: 270,
        complete: () => { appLauncher.$ul.addClass('complete') }
      })
    })

    appLauncher.$toggle.append(
      $('<span class="icon-label pt-6">').text(gt('Menu'))
    ).addClass('flex-col')

    this.$el.empty().append(
      this.renderAppLaunchers(),
      appLauncher.render().$el
    )
    this.$el.find('button').first().removeAttr('tabindex')
    return this
  }
})

ext.point('io.ox/core/appcontrol/mobile/top').extend({
  id: 'appcontrol-mobile',
  draw () {
    if (!_.device('smartphone')) return
    this.addClass('flex-col')
  }
})

ext.point('io.ox/core/appcontrol/mobile/top').extend({
  id: 'searchcontainers-mobile',
  draw () {
    if (!_.device('smartphone')) return
    const searchContainers = $('<div class="io-ox-search-containers">')
    searchContainers.insertBefore($('#io-ox-screens'))
  }
})

ext.point('io.ox/core/appcontrol/mobile/bottom').extend({
  id: 'app-navigation-mobile',
  draw () {
    if (!_.device('smartphone')) return
    const navigation = new AppNavigationView()
    const $offlineIndicator = $('<div id="io-ox-offline-smartphone" role="alert">')
    this.append($offlineIndicator, navigation.render().$el)
  }
})

function initRefreshAnimation () {
  let count = 0
  let timer = null

  // unprompted animations can be challenging for users with attention deficit disorders,
  // so we'll only do it if the user has not set their system to reduce motion
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  let useSpinner = !mediaQuery.matches
  mediaQuery.addEventListener('change', e => { useSpinner = !e.matches })

  const duration = useSpinner ? 500 : 1500
  let refreshIcon = $()

  function off () {
    if (count === 0 && timer === null) {
      $('#io-ox-refresh-icon .apptitle').attr('aria-label', gt('Refresh'))

      if (useSpinner) {
        refreshIcon = refreshIcon.length ? refreshIcon : $('#io-ox-refresh-icon').find('svg')
        if (refreshIcon.hasClass('animate-spin')) {
          refreshIcon.addClass('animation-paused')
          let done = false
          setTimeout(function () { done = true }, 2546)
          refreshIcon.on('animationiteration', function (event) {
            if (done) $(event.target).removeClass('animate-spin')
          })
        }
      }
    }
  }

  http.on('start', function (e, xhr, options) {
    if (count === 0) {
      if (timer === null && !options.silent) {
        $('#io-ox-refresh-icon .apptitle').attr('aria-label', gt('Currently refreshing'))

        if (useSpinner) {
          refreshIcon = refreshIcon.length ? refreshIcon : $('#io-ox-refresh-icon').find('svg')
          if (!refreshIcon.hasClass('animate-spin')) {
            refreshIcon.addClass('animate-spin').removeClass('animation-paused')
          }
        }
      }
      clearTimeout(timer)
      timer = setTimeout(function () {
        timer = null
        off()
      }, duration)
    }
    count++
  })

  http.on('stop', function () {
    count = Math.max(0, count - 1)
    off()
  })
}

export {
  getQuickLaunchSettingsKey,
  getQuickLauncherItems,
  getQuickLauncherCount,
  isQuickLaunchConfigurable
}

export default _.extend(api, {
  LauncherView,
  LaunchersView
})
